import { Draggable, Droppable } from 'react-beautiful-dnd';
import { Typography, Wrapper } from '@screentone/core';
import classnames from 'classnames';

// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { Article } from 'components/datamodel/content/Article/Article';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { LinkedItem as LinkedItemComponent } from 'components/datamodel/content/LinkedItem/LinkedItem';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { DRAGGABLE_PREFIXES, DROPPABLE_ID_PREFIXES } from 'contexts/drag-and-drop/dragUtils';
import { ArticleItem, ContentUnion, LinkedItem } from 'data/generated/graphql';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { isContentIdExcludedInQueryRules } from 'utils/queryUtils';
import { safelyParseContent } from 'utils/temp';
import styles from './ManualItems.module.scss';

import { EntityType } from 'contexts/datamodel/DataModelContext.constants';

interface ManualItemsProps {
  articles: ContentUnion[];
  allItems: ContentUnion[];
  allessehJsonQuery: string | null;
  setJsonQueryStr: (newQueryStr: string) => void;
  listHierarchyId: string;
  isReadOnly?: boolean;
  isHistory?: boolean;
}

export const ManualItems = ({
  articles,
  allItems,
  allessehJsonQuery,
  setJsonQueryStr,
  listHierarchyId,
  isReadOnly = false,
  isHistory = false
}: ManualItemsProps) => {
  const { generateHierarchyId } = useDataModelContext();
  const { renderActions } = useContextMenuActions();

  return (
    <Droppable
      droppableId={
        (isHistory ? DROPPABLE_ID_PREFIXES.HISTORY : DROPPABLE_ID_PREFIXES.COLLECTION_ITEMS) + listHierarchyId
      }
      isDropDisabled={isReadOnly || isHistory}
    >
      {(droppableProvided, snapshot) => (
        <div
          data-testid="curated-list-container"
          className={classnames(styles.itemsList, { [styles.itemsListEmpty]: articles.length === 0 })}
          ref={droppableProvided.innerRef}
          {...droppableProvided.droppableProps}
        >
          {articles.length === 0 ? (
            <Wrapper
              data-testid="curated-list-empty-list"
              className={classnames(styles.emptyCuratedList, {
                [styles.emptyCuratedListDragging]: snapshot.isDraggingOver
              })}
              padding={{ all: 'md' }}
            >
              <Typography data-testid="curated-list-empty-label" variant="note">
                {isHistory ? 'No items in the Curated list' : 'Drop story here'}
              </Typography>
            </Wrapper>
          ) : (
            articles.map((article, filteredIndex) => {
              const realIndex = allItems.findIndex((item) => item === article);
              const fullHierarchyId = generateHierarchyId(article, listHierarchyId, realIndex);
              return (
                <div
                  key={
                    (isHistory ? DRAGGABLE_PREFIXES.HISTORY_MODULE_ITEM : DRAGGABLE_PREFIXES.COLLECTION_ITEM) +
                    fullHierarchyId +
                    article.attributes.id
                  }
                >
                  <Draggable
                    draggableId={
                      isHistory
                        ? DRAGGABLE_PREFIXES.HISTORY_MODULE_ITEM + JSON.stringify(article)
                        : DRAGGABLE_PREFIXES.COLLECTION_ITEM + fullHierarchyId + article.attributes.id
                    }
                    index={filteredIndex}
                    isDragDisabled={isReadOnly}
                  >
                    {(draggableProvided) => (
                      <div
                        data-testid="curated-list-item"
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                      >
                        {article.type === EntityType.LinkedItem ? (
                          <LinkedItemComponent
                            data={article as LinkedItem}
                            parentHierarchyId={listHierarchyId}
                            index={realIndex}
                          />
                        ) : (
                          <Article
                            data={article as ArticleItem}
                            parentHierarchyId={listHierarchyId}
                            index={realIndex}
                            isDraft
                            isHistory={false}
                            isCollectionItem
                            renderActions={() =>
                              renderActions(MenuActionsKeys.DraftArticle, {
                                entity: article,
                                hierarchyId: fullHierarchyId,
                                index: realIndex,
                                extraProperties: {
                                  allessehContent: safelyParseContent<AllessehContent>(article),
                                  jsonQueryStr: allessehJsonQuery ?? undefined,
                                  setJsonQueryStr,
                                  isContentIdExcludedInQueryRules: isContentIdExcludedInQueryRules(
                                    article.attributes.id,
                                    allessehJsonQuery
                                  )
                                }
                              })
                            }
                          />
                        )}
                      </div>
                    )}
                  </Draggable>
                </div>
              );
            })
          )}
          {droppableProvided.placeholder}
        </div>
      )}
    </Droppable>
  );
};
